/*
 * SeatFit reviews data: loads the review index + review files and hands out reviews in display order.
 * Every product kind has its own bank (written by scripts/import_owner_review_list_to_theme_assets.py --kind,
 * next to this script), named after its index: seat covers 'seatfit-reviews-', car covers 'coverfit-reviews-',
 * floor mats 'matfit-reviews-' (the reviewsIndexUrl of each kind in snippets/seatfit-config.liquid):
 *   <prefix>index.json   {v,total,avg,dist,fit,quality,makes:{slug:{count,h,top}},general:{count,h,top}}
 *   <prefix>general.json + <prefix><make>.json   {v, reviews:[{n,r,t,b,d,p,mk,md,y,vt,src,fit?,quality?}]}
 * Order: same model -> same make -> the rest; inside a tier photos first, then newest first.
 * General + the current make are fetched before the first paint. Another make's file is fetched
 * only when its best review (index "top") would rank inside the next batch, so the order stays
 * exact across files while a shopper with enough reviews for their own car costs 3 requests.
 */
(function () {
  'use strict';
  var NS = window.SeatFitReviews = window.SeatFitReviews || {};
  if (NS.data) return;

  var cache = {};

  function getJSON(url) {
    if (!cache[url]) {
      cache[url] = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status + ' for ' + url);
        return r.json();
      }).catch(function (e) {
        delete cache[url]; // a network blip must not poison the rest of the visit
        throw e;
      });
    }
    return cache[url];
  }

  // The index of the product this page sells: its kind's reviewsIndexUrl from the core config (the product block
  // decides the kind), else the URL the section printed. A car cover page must never show the seat cover bank.
  function indexUrl(fallback) {
    var SF = window.SeatFit;
    var kc = SF && SF.kindConfig ? SF.kindConfig() : null;
    return (kc && kc.reviewsIndexUrl) || fallback || '';
  }

  // Shopify asset URLs share one folder, so the index URL '<base><prefix>index.json?v=..' gives the folder and
  // the name prefix of every review file of that bank.
  function filesOf(url) {
    url = String(url || '');
    var name = url.split('?')[0].split('/').pop();
    var prefix = /index\.json$/.test(name) ? name.replace(/index\.json$/, '') : 'seatfit-reviews-';
    return { base: name ? url.slice(0, url.lastIndexOf(name)) : url, prefix: prefix };
  }

  // The hash from the index changes whenever a file changes. It rides on ?v= because Shopify's CDN keys
  // its cache on that param only (any other name is ignored and a year-old copy could be served).
  function fileUrl(files, key, info) {
    return files.base + files.prefix + key + '.json' + (info && info.h ? '?v=' + encodeURIComponent(info.h) : '');
  }

  function validReview(rv) {
    var r = rv && Number(rv.r);
    return !!(rv && typeof rv.b === 'string' && rv.b.trim() && r >= 1 && r <= 5);
  }

  function tierOf(rv, veh) {
    if (!veh.make || rv.mk !== veh.make) return 2;
    return veh.model && rv.md === veh.model ? 0 : 1;
  }

  function compare(a, b) {
    if (a.tier !== b.tier) return a.tier - b.tier;
    if (a.pics !== b.pics) return b.pics - a.pics;
    if (a.d !== b.d) return a.d < b.d ? 1 : -1; // ISO dates sort as text
    return a.seq - b.seq;
  }

  function Pool(index, indexUrl, veh) {
    var self = this;
    this.total = Number(index.total) || 0;
    this.veh = { make: (veh && veh.make) || null, model: (veh && veh.model) || null };
    this.files = filesOf(indexUrl);
    this.queue = [];
    this.shownCount = 0;
    this.seq = 0;
    var first = [];
    var rest = [];
    if (index.general && index.general.count > 0) first.push(['general', index.general]);
    var makes = index.makes || {};
    Object.keys(makes).forEach(function (k) {
      if (!(makes[k] && makes[k].count > 0)) return;
      (k === self.veh.make ? first : rest).push([k, makes[k]]);
    });
    // Files whose best review ranks highest first: they are the ones "the rest" tier needs soonest.
    rest.sort(function (a, b) {
      var ra = topRank(a[1].top);
      var rb = topRank(b[1].top);
      return ra !== rb ? (ra < rb ? 1 : -1) : b[1].count - a[1].count;
    });
    this.pending = rest;
    this.first = first; // loaded together before the first paint so tiers are right at once
  }

  // top = [has photos 0|1, newest date among those] of a file's best review, written by the
  // importer into the index. A file without it (older index) must be assumed to beat anything.
  function beats(top, other) {
    if (!Array.isArray(top)) return true;
    if (!Array.isArray(other)) return false;
    var p = top[0] ? 1 : 0;
    var q = other[0] ? 1 : 0;
    return p !== q ? p > q : String(top[1] || '') > String(other[1] || '');
  }

  // Sortable text for the same order ("2" = unknown, loaded first because it may beat anything).
  function topRank(top) {
    return Array.isArray(top) ? (top[0] ? '1' : '0') + String(top[1] || '') : '2';
  }

  // Every pending file is another make, so all its reviews sit in "the rest" tier. A file only
  // has to load when fewer than n reviews are queued or its best review would outrank the n-th.
  Pool.prototype.needed = function (n) {
    var cut = this.queue.length >= n ? this.queue[n - 1] : null;
    if (!cut) return this.pending.slice(0, 3);
    if (cut.tier < 2) return [];
    return this.pending.filter(function (f) { return beats(f[1].top, [cut.pics, cut.d]); }).slice(0, 6);
  };

  Pool.prototype.hasMore = function () {
    return this.queue.length > 0 || this.pending.length > 0 || this.first.length > 0;
  };

  // Counted from the index total, so the button can say how many a click adds before files load.
  Pool.prototype.remaining = function () {
    return Math.max(0, this.total - this.shownCount);
  };

  Pool.prototype.add = function (list) {
    var self = this;
    (list || []).forEach(function (rv) {
      if (!validReview(rv)) return;
      self.queue.push({
        rv: rv, tier: tierOf(rv, self.veh), pics: rv.p && rv.p.length ? 1 : 0,
        d: String(rv.d || ''), seq: self.seq++
      });
    });
    // Only the not-yet-shown queue is re-sorted: cards already on screen never jump around.
    this.queue.sort(compare);
  };

  Pool.prototype.loadFiles = function (batch) {
    var self = this;
    this.pending = this.pending.filter(function (f) { return batch.indexOf(f) < 0; });
    return Promise.all(batch.map(function (f) {
      return getJSON(fileUrl(self.files, f[0], f[1])).then(function (data) {
        self.add(data && data.reviews);
      }).catch(function (e) {
        console.warn('[SeatFit reviews] skipped review file "' + f[0] + '"', e);
      });
    }));
  };

  // Cards once shown never move, so nothing is handed out while a pending file could outrank it.
  Pool.prototype.ensure = function (n) {
    var self = this;
    var start = this.first.length ? this.loadFiles(this.first) : Promise.resolve();
    this.first = [];
    return start.then(function loop() {
      var batch = self.needed(n);
      return batch.length ? self.loadFiles(batch).then(loop) : null;
    });
  };

  Pool.prototype.take = function (n) {
    var self = this;
    return this.ensure(n).then(function () {
      var out = self.queue.splice(0, n);
      self.shownCount += out.length;
      return out.map(function (w) { return w.rv; });
    });
  };

  NS.data = {
    indexUrl: indexUrl,
    loadIndex: function (url) { return url ? getJSON(url) : Promise.reject(new Error('no review index URL')); },
    createPool: function (index, url, veh) { return new Pool(index, url, veh); },
    filesOf: filesOf,
    tierOf: tierOf
  };
})();
